import React, { useState, useEffect } from "react";
import styles from "./Statements.module.sass";
import Contract from "./Contract";
import Convention from "./Convention";
import Rupture from "./Rupture";
import { checkSignatureStatus } from "../../../services/signature.service";

const ContractDetail = ({ folderId }) => {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [conventionStatus, setConventionStatus] = useState(null);
  const [ruptureStatus, setRuptureStatus] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Vérifier les statuts de signature
  useEffect(() => {
    const checkSignatures = async () => {
      if (contract?.procedureCerfa) {
        try {
          const status = await checkSignatureStatus(contract.procedureCerfa);
          setSignatureStatus(status);
        } catch (err) {
          console.error("Erreur lors de la vérification du statut de signature du CERFA:", err);
        }
      }

      if (contract?.procedureConvention) {
        try {
          const status = await checkSignatureStatus(contract.procedureConvention);
          setConventionStatus(status);
        } catch (err) {
          console.error("Erreur lors de la vérification du statut de signature de la convention:", err);
        }
      }

      if (contract?.procedureRupture) {
        try {
          const status = await checkSignatureStatus(contract.procedureRupture);
          setRuptureStatus(status);
        } catch (err) {
          console.error("Erreur lors de la vérification du statut de signature de la rupture:", err);
        }
      }
    };

    if (contract) {
      checkSignatures();
    }
  }, [contract]);

  // Récupérer le contrat depuis le folderId
  useEffect(() => {
    const fetchContractFromFolder = async () => {
      if (!folderId) {
        setError("ID de dossier non valide");
        setIsInitialized(true);
        return;
      }

      // Si déjà initialisé avec ce folderId, ne pas recharger
      if (isInitialized && contract) {
        console.log("Contrat déjà chargé, pas de rechargement nécessaire");
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        console.log("Récupération du contrat depuis le folder ID:", folderId);
        
        // Récupérer le folder avec toutes ses relations, y compris le contrat
        const { pb } = await import('../../../services/pocketbase');
        const folder = await pb.collection('folders').getOne(folderId, {
          expand: 'contract_id,center_id'
        });
        
        if (folder?.contract_id) {
          // Récupérer le contrat complet avec ses relations
          const contractData = await pb.collection('contracts').getOne(folder.contract_id, {
            expand: 'student_id,company_id,tutor_id'
          });
          
          // Ajouter les informations du centre depuis le folder
          contractData.expand = {
            ...contractData.expand,
            center_id: folder.expand?.center_id || null
          };
          
          console.log("Contrat récupéré avec centre depuis le folder:", contractData);
          setContract(contractData);
        } else {
          console.log("Aucun contrat associé à ce dossier");
          setError("Aucun contrat associé à ce dossier");
        }
      } catch (err) {
        console.error("Erreur lors de la récupération du contrat depuis le folder:", err);
        setError("Impossible de charger les détails du contrat");
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    fetchContractFromFolder();
  }, [folderId]); // Supprimer les dépendances qui causent des boucles

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loading}>Chargement des détails du contrat...</div>
      </div>
    );
  }

  if (!contract) {
    return null;
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.section}>
          <Contract 
            className={styles.card} 
            contract={contract} 
            signatureStatus={signatureStatus}
            onContractUpdate={setContract}
            folderId={folderId}
          />
          <Convention 
            className={styles.card} 
            contract={contract} 
            signatureStatus={conventionStatus}
            onContractUpdate={setContract}
            folderId={folderId}
          />
          {contract.rupture && (
            <Rupture
              className={styles.card}
              contract={contract}
              signatureStatus={ruptureStatus}
              onContractUpdate={setContract}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ContractDetail;
